import { useEffect, useState } from "react";

// Overlay de diagnostico de resolucion de imagenes. NO se monta salvo que la URL traiga
// ?imgdebug=1, asi que es inerte en produccion normal.
//
// Objetivo: permitir medir la pixelacion en las pantallas fisicas sin acceso remoto.
// Alguien en sitio abre la web con ?imgdebug=1 en la pantalla grande y ve, sobre cada
// foto, un badge con la resolucion real del archivo (naturalWxH), el tamano de despliegue
// y el factor de "upscale" = (ancho_css * devicePixelRatio) / naturalWidth.
//   upscale <= 1  -> la imagen tiene pixeles de sobra (nitida)
//   upscale > 1.15 -> se esta ampliando -> se vera pixelada (badge en rojo)
//
// Cubre solo imagenes de vehiculos grandes (filtra iconos/logos por tamano de despliegue).

interface Badge {
    left: number;
    top: number;
    text: string;
    bad: boolean;
}

const MIN_RENDER_WIDTH = 120; // ignora iconos y miniaturas chiquitas

const isDebugActive = (): boolean => {
    try {
        return new URLSearchParams(window.location.search).get("imgdebug") === "1";
    } catch {
        return false;
    }
};

const ImgDebugOverlay = () => {
    const [active, setActive] = useState<boolean>(isDebugActive());
    const [badges, setBadges] = useState<Badge[]>([]);
    const [dpr, setDpr] = useState<number>(typeof window !== "undefined" ? window.devicePixelRatio : 1);

    // Reevalua el flag si cambia la URL (navegacion SPA).
    useEffect(() => {
        const onNav = () => setActive(isDebugActive());
        window.addEventListener("popstate", onNav);
        return () => window.removeEventListener("popstate", onNav);
    }, []);

    useEffect(() => {
        if (!active) return;

        const scan = () => {
            setDpr(window.devicePixelRatio);
            const next: Badge[] = [];
            document.querySelectorAll("img").forEach((img) => {
                if (img.dataset.imgdebug === "1") return; // no medirse a si mismo (por si acaso)
                const rect = img.getBoundingClientRect();
                if (rect.width < MIN_RENDER_WIDTH || img.naturalWidth === 0) return;
                if (rect.bottom < 0 || rect.top > window.innerHeight) return; // fuera de viewport
                const needAtDpr = Math.round(rect.width * window.devicePixelRatio);
                const upscale = needAtDpr / img.naturalWidth;
                next.push({
                    left: rect.left,
                    top: rect.top,
                    text: `nat ${img.naturalWidth}x${img.naturalHeight} · css ${Math.round(rect.width)}x${Math.round(
                        rect.height,
                    )} · ${upscale.toFixed(2)}x`,
                    bad: upscale > 1.15,
                });
            });
            setBadges(next);
        };

        scan();
        const interval = setInterval(scan, 1000);
        window.addEventListener("scroll", scan, true);
        window.addEventListener("resize", scan);
        return () => {
            clearInterval(interval);
            window.removeEventListener("scroll", scan, true);
            window.removeEventListener("resize", scan);
        };
    }, [active]);

    if (!active) return null;

    return (
        <div style={{ position: "fixed", inset: 0, zIndex: 999999, pointerEvents: "none" }}>
            <div
                style={{
                    position: "fixed",
                    top: 8,
                    left: 8,
                    background: "#000",
                    color: "#0f0",
                    font: "12px/1.4 monospace",
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "1px solid #0f0",
                }}
            >
                imgdebug · DPR {dpr} · viewport {window.innerWidth}x{window.innerHeight} · rojo = se amplia (pixelado)
            </div>
            {badges.map((b, i) => (
                <div
                    key={i}
                    style={{
                        position: "fixed",
                        left: b.left,
                        top: b.top,
                        background: b.bad ? "rgba(200,0,0,0.92)" : "rgba(0,120,0,0.9)",
                        color: "#fff",
                        font: "11px/1.3 monospace",
                        padding: "2px 5px",
                        maxWidth: "90vw",
                        whiteSpace: "nowrap",
                        borderBottomRightRadius: 5,
                    }}
                >
                    {b.text}
                </div>
            ))}
        </div>
    );
};

export default ImgDebugOverlay;
